import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LazyControlBoundary } from "@/main/react/LazyControlBoundary";

// Type-only import() query: erased at runtime, so it pulls nothing into the eager graph while still
// giving the exact prop types.
type ProfileShowcaseProps = React.ComponentProps<
    (typeof import("@/main/react/user/ProfileShowcase"))["ProfileShowcase"]
>;

/**
 * The profile showcase, code-split behind the chunk-resilience helper (AGENTS.md rule 26).
 *
 * Split for the same two reasons as the trophy rail. It keeps the curation dialog — Radix Dialog and the
 * four candidate lists — off the startup path (rule 27's eager budget), and it keeps them off the path of
 * every profile view rather than only the ones where the owner opens the dialog. A profile is the most
 * crawled page on the site, so nothing here may be something the page has to load before it can render.
 *
 * `lazyWithRetry` rather than a bare `import()`: a bare one has no recovery, and a browser holding a
 * stale `main_bundle.js` that references a chunk hash the origin no longer serves would leave this
 * silently unmounted for the life of that tab. `MountErrorBoundary` does not cover that — the rejection
 * happens in a fire-and-forget closure, outside React's render tree.
 */
const ProfileShowcaseImpl = lazyWithRetry(
    () =>
        import("@/main/react/user/ProfileShowcase").then((m) => ({
            default: m.ProfileShowcase,
        })),
    "profile-showcase",
);

/**
 * No skeleton, deliberately — and here that is stronger than a preference.
 *
 * A profile with nothing featured must render NOTHING to a visitor: no heading, no card, no placeholder.
 * A pulsing skeleton would put a box on every profile that has no showcase at all, which is the empty
 * trophy case the design bans, arriving one frame at a time. `null` means the block appears only once
 * there is genuinely something to appear.
 */
export function ProfileShowcaseLazy(props: ProfileShowcaseProps) {
    // Containment (2026-09-15): a chunk that never arrives must cost the showcase, not the UserPage
    // island. The boundary sits OUTSIDE the Suspense, where the lazy rejection is re-thrown to. `null`
    // is the honest fallback here for the same reason it is the Suspense fallback: the block appears
    // only once there is genuinely something to appear, and a profile without it is complete.
    return (
        <LazyControlBoundary controlName="profile-showcase" fallback={null}>
            <Suspense fallback={null}>
                <ProfileShowcaseImpl {...props} />
            </Suspense>
        </LazyControlBoundary>
    );
}

export default ProfileShowcaseLazy;
